import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";
import { locationIdFormat } from "../../ids";
import { WarehouseGridSchema } from "./warehouse-grid";

export const DistanceMatrixSchema = z.record(
  z.string(),
  z.record(z.string(), z.union([z.number(), z.literal(Infinity)])),
);
export type DistanceMatrix = z.infer<typeof DistanceMatrixSchema>;

/**
 * Compacts a symmetric distance matrix to only store upper triangle (where from < to).
 * This roughly halves the storage size.
 */
export function compactDistanceMatrix(matrix: DistanceMatrix): DistanceMatrix {
  const compact: DistanceMatrix = {};
  for (const from of Object.keys(matrix)) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    for (const to of Object.keys(matrix[from])) {
      if (from < to) {
        compact[from] ??= {};
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        compact[from][to] = matrix[from][to];
      }
    }
  }
  return compact;
}

/**
 * Expands a compacted triangular distance matrix back to full symmetric matrix.
 */
export function expandDistanceMatrix(compact: DistanceMatrix): DistanceMatrix {
  const full: DistanceMatrix = {};
  const allKeys = new Set<string>();

  for (const from of Object.keys(compact)) {
    allKeys.add(from);
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    for (const to of Object.keys(compact[from])) {
      allKeys.add(to);
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const dist = compact[from][to];
      full[from] ??= {};
      full[to] ??= {};
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      full[from][to] = dist;
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      full[to][from] = dist;
    }
  }

  // Add self-distances (0)
  for (const key of allKeys) {
    full[key] ??= {};
    full[key][key] = 0;
  }

  return full;
}

/**
 * Checks if a distance matrix is in compact (triangular) format.
 * A compact matrix only has entries where from < to.
 */
export function isCompactDistanceMatrix(matrix: DistanceMatrix): boolean {
  for (const from of Object.keys(matrix)) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    for (const to of Object.keys(matrix[from])) {
      if (from >= to) {
        return false;
      }
    }
  }
  return true;
}

export const WarehouseLayoutSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  crdbLocationId: locationIdFormat.schema.nullish(),
  locationId: zExt.objectId().nullish(),
  warehouse: WarehouseGridSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
  createdBy: zExt.objectId(),
  deletedAt: z.date().nullish(),
});

export type WarehouseLayout = z.infer<typeof WarehouseLayoutSchema>;
export interface IWarehouseLayoutSchema extends z.ZodType<WarehouseLayout> {}
